import { useState } from "react";
import { Forward, Inbox, MailCheck } from "lucide-react";
import { clsx } from "clsx";
import type { NotificationDelegation } from "../api/settings";
import {
  useDelegationCandidates,
  useDelegationMutations,
  useNotificationDelegations,
} from "../hooks/useSettings";

/**
 * Przekazywanie powiadomień — karta w Ustawieniach konta.
 *
 * Góra: moje własne przekazanie (jedno naraz). Bez niego pokazujemy wybór osoby
 * z listy kandydatów (ten sam oddział, ta sama lub wyższa rola — reguła siedzi
 * w backendzie, front tylko wyświetla to, co dostał). Z nim — stan prośby
 * (oczekuje / aktywne) i przycisk cofnięcia.
 *
 * Dół: cudze przekazania skierowane do mnie — prośby do zaakceptowania albo
 * odrzucenia oraz aktywne przejęcia, które mogę w każdej chwili zakończyć.
 *
 * „Tryb urlopowy" (sync_read) to decyzja przekazującego: kopia odczytana przez
 * zastępcę znika też z jego nieprzeczytanych. Zastępca tylko o tym wie.
 */

const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "";

function errorMessage(err: unknown): string {
  const errors = (err as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors as Record<string, unknown>)[0];
    const message = Array.isArray(first) ? first[0] : first;
    if (typeof message === "string" && message) return message;
  }
  return "Nie udało się zapisać zmiany. Spróbuj ponownie.";
}

const primaryBtn =
  "px-3 py-1.5 text-xs rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white disabled:opacity-50 whitespace-nowrap";
const secondaryBtn =
  "px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 whitespace-nowrap";

const SYNC_READ_LABEL = "Odczytane u zastępcy = odczytane u mnie";
const SYNC_READ_HINT =
  "Tryb urlopowy: gdy zastępca odczyta przekazane powiadomienie, znika ono też z Twoich nieprzeczytanych.";

function SyncReadCheckbox({
  id,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label htmlFor={id} className="flex items-start gap-2 cursor-pointer select-none">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-[var(--color-primary)] focus:ring-[var(--color-primary)] disabled:opacity-50"
      />
      <span>
        <span className="block text-sm text-gray-700 dark:text-gray-300">{SYNC_READ_LABEL}</span>
        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{SYNC_READ_HINT}</span>
      </span>
    </label>
  );
}

function PersonLine({ person }: { person: NotificationDelegation["delegator"] }) {
  return (
    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
      {person.full_name}
      <span className="text-xs font-normal text-gray-500 dark:text-gray-400"> · {person.role_label}</span>
    </p>
  );
}

export function NotificationDelegationSection() {
  const { data, isLoading, isError } = useNotificationDelegations();
  const outgoing = data?.outgoing ?? null;
  const incoming = data?.incoming ?? [];
  const showPicker = !!data && !outgoing;
  const { data: candidates, isLoading: candidatesLoading } = useDelegationCandidates(showPicker);
  const { create, accept, decline, revoke, setSyncRead } = useDelegationMutations();

  const [selected, setSelected] = useState("");
  const [syncRead, setSyncRead_] = useState(false);
  const [error, setError] = useState("");

  const busy =
    create.isPending ||
    accept.isPending ||
    decline.isPending ||
    revoke.isPending ||
    setSyncRead.isPending;

  const run = (action: () => Promise<unknown>) => {
    setError("");
    action().catch((err: unknown) => setError(errorMessage(err)));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    run(() =>
      create.mutateAsync({ delegate: selected, sync_read: syncRead }).then(() => {
        setSelected("");
        setSyncRead_(false);
      }),
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-6">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
        Przekazywanie powiadomień
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Wskaż osobę z Twojego oddziału o tej samej lub wyższej roli, która będzie dostawać kopie
        wszystkich Twoich powiadomień. Przekazywanie rusza po jej akceptacji i trwa, aż jedna ze
        stron je cofnie. Ty nadal widzisz swoje powiadomienia.
      </p>

      {isLoading && <p className="text-xs text-gray-400">Wczytywanie…</p>}
      {isError && (
        <p className="text-xs text-red-600 dark:text-red-400">
          Nie udało się wczytać ustawień przekazywania.
        </p>
      )}

      {outgoing && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-2 min-w-0">
              <Forward size={14} className="text-[var(--color-primary)] mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <PersonLine person={outgoing.delegate} />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {outgoing.status === "pending"
                    ? `Prośba wysłana ${fmtDate(outgoing.created_at)} — czeka na akceptację.`
                    : `Dostaje kopie Twoich powiadomień od ${fmtDate(outgoing.responded_at)}.`}
                </p>
              </div>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => run(() => revoke.mutateAsync(outgoing.id))}
              className={secondaryBtn}
            >
              {outgoing.status === "pending" ? "Wycofaj prośbę" : "Cofnij przekazanie"}
            </button>
          </div>
          <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
            <SyncReadCheckbox
              id="notification-delegation-sync-read-outgoing"
              checked={outgoing.sync_read}
              disabled={busy}
              onChange={(value) =>
                run(() => setSyncRead.mutateAsync({ id: outgoing.id, sync_read: value }))
              }
            />
          </div>
        </div>
      )}

      {showPicker && (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label
              htmlFor="notification-delegate"
              className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
            >
              Osoba przejmująca powiadomienia
            </label>
            <select
              id="notification-delegate"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              disabled={candidatesLoading || !candidates?.users.length}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] disabled:opacity-60"
            >
              <option value="">
                {candidatesLoading ? "Wczytywanie…" : "— wybierz osobę —"}
              </option>
              {candidates?.users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name} · {u.role_label}
                </option>
              ))}
            </select>
            {candidates && candidates.users.length === 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">
                {candidates.hasBranch
                  ? "W Twoim oddziale nie ma nikogo o tej samej lub wyższej roli, komu można przekazać powiadomienia."
                  : "Twoje konto nie ma przypisanego oddziału — poproś dyrektora o jego ustawienie."}
              </p>
            )}
          </div>
          <SyncReadCheckbox
            id="notification-delegation-sync-read"
            checked={syncRead}
            disabled={busy}
            onChange={setSyncRead_}
          />
          <div className="flex justify-end">
            <button type="submit" disabled={!selected || busy} className={primaryBtn}>
              {create.isPending ? "Wysyłanie…" : "Wyślij prośbę"}
            </button>
          </div>
        </form>
      )}

      {error && (
        <p role="alert" className="text-xs text-red-600 dark:text-red-400 mt-3">
          {error}
        </p>
      )}

      {incoming.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            <Inbox size={12} />
            Powiadomienia innych osób
          </h3>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {incoming.map((d) => (
              <li key={d.id} className="flex items-start justify-between gap-4 py-2.5">
                <div className="min-w-0">
                  <PersonLine person={d.delegator} />
                  <p
                    className={clsx(
                      "text-xs mt-0.5",
                      d.status === "pending"
                        ? "text-[var(--color-primary)]"
                        : "text-gray-500 dark:text-gray-400",
                    )}
                  >
                    {d.status === "pending"
                      ? "Prosi Cię o przejmowanie swoich powiadomień."
                      : `Dostajesz kopie od ${fmtDate(d.responded_at)}.`}
                  </p>
                  {d.sync_read && (
                    <p className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      <MailCheck size={12} className="flex-shrink-0" />
                      Gdy odczytasz kopię, u tej osoby też będzie oznaczona jako przeczytana.
                    </p>
                  )}
                </div>
                {d.status === "pending" ? (
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => run(() => accept.mutateAsync(d.id))}
                      className={primaryBtn}
                    >
                      Akceptuj
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => run(() => decline.mutateAsync(d.id))}
                      className={secondaryBtn}
                    >
                      Odrzuć
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => run(() => revoke.mutateAsync(d.id))}
                    className={secondaryBtn}
                  >
                    Zakończ
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
